import Box from 'ext-ui/components/Box/Box'
import ProgressBar from 'ext-ui/components/ProgressBar/ProgressBar'
import React, { ReactElement, ReactNode, useId } from 'react'
import * as style from 'src/feature/CardOverviewStats/cardOverviewStats.style'
import {
  formatStatAmount,
  formatStatDate,
  formatStatDateShort,
  formatUtilization,
} from 'src/feature/CardOverviewStats/cardOverviewStatsFormat'
import StatNavLink from 'src/feature/CardOverviewStats/StatNavLink'
import { useCardOverviewStatsData } from 'src/feature/CardOverviewStats/useCardOverviewStatsData'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import PluralTrns from 'src/localization/PluralTrns'
import { useTranslate } from 'src/localization/Trns'

/** Rendered wherever a figure is not available, for any reason. */
const EM_DASH = '—'

export type CardOverviewStatsProps = {
  /** The card whose limits to read. */
  creditCardId: string
  /**
   * Whether to show the Virtual cards cell, and whether to show the budgets
   * footer.
   *
   * TWO flags, not one. Each of these links to a different destination, and the
   * page's conditions for those two destinations genuinely differ — a closed
   * card, for instance, keeps virtual cards but loses budgets. A single
   * combined flag silently mismatched one of them.
   *
   * Both are suppressed rather than emptied when false: an em dash beside
   * "View virtual cards →" still offers a link to a surface the card does not
   * have and the subnav cannot show as active.
   *
   * Capabilities rather than a card taxonomy, because this component is
   * routing-free and has no business knowing what a physical-only card is.
   */
  showVirtualCards: boolean
  showBudgets: boolean
  /**
   * Active virtual-card count, and total budget count.
   *
   * Supplied by the page rather than fetched here: they live in the legacy
   * per-credit-card statistics family, whose selectors are route-coupled, and a
   * feature must not read routing. `undefined` renders as an em dash — see
   * `useCardOverviewStatsData` for the full reasoning.
   */
  virtualCardCount: number | undefined
  budgetCount: number | undefined
  /** Destinations, built by the page. */
  adjustLimitHref: string
  virtualCardsHref: string
  budgetsHref: string
  /** SPA navigation for all three links. */
  onNavigate?: (href: string) => void
  className?: string
}

type StatCellProps = {
  label: string
  value: string
  /** Secondary line, a link, or the utilization bar. */
  sub?: ReactNode
  labelId?: string
  testId: string
}

/**
 * One label/value pair. A `<div>` inside the roll-up's `<dl>`, which is the
 * grouping wrapper the spec allows between `<dl>` and its `<dt>`/`<dd>` pairs —
 * it is what lets each pair be a grid cell without breaking the list semantics.
 */
function StatCell(props: StatCellProps): ReactElement {
  const { label, value, sub, labelId, testId } = props

  return (
    <div css={style.cell} data-testid={testId}>
      <dt id={labelId} css={style.cellLabel}>
        {label}
      </dt>
      <dd css={style.cellValue}>{value}</dd>
      {sub !== undefined && <dd css={style.cellSub}>{sub}</dd>}
    </div>
  )
}

/**
 * The financial and usage summary at the top of a card's Overview tab
 * (EX-40632): balance, payment due, statement close, limit and utilization
 * always; the virtual-card count and budgets footer only when the card can have
 * them (see `showVirtualCards` / `showBudgets`).
 *
 * ROUTING-FREE — every destination arrives as an href prop and the page owns
 * both the URL construction and the SPA navigation.
 *
 * NOTHING HERE IS FABRICATED. Every figure is read from an endpoint gated on its
 * own capability, and a group that cannot be read renders an em dash rather than
 * a stand-in. EX-40596 removed the last of the invented numbers.
 *
 * Balance, card limit, and utilization read `READ_PHYSICAL_CARD_LIMIT`; payment
 * due reads `READ_PHYSICAL_CARD_PAYMENTS` and statement close
 * `READ_PHYSICAL_CARD_STATEMENTS` (see `useCardOverviewStatsData`). While the
 * payment and statement endpoints are mock-backed, the roll-up shows a text
 * provenance indicator rather than relying on colour alone. The virtual-card and
 * budget counts come from a Redux statistics family that is frequently
 * unpopulated, so they render as em dashes as often as numbers.
 *
 * A card with no `READ_PHYSICAL_CARD_LIMIT` — an org holding
 * `show_updated_card_overview` on without being enrolled in Card Management —
 * therefore sees em dashes across most of the row. That is the honest reading,
 * and it is worth knowing before enabling the flag for a demo.
 *
 * The allocated-budget total that used to sit in the budgets footer was dropped
 * rather than faked: no aggregate for it exists anywhere in the API, and summing
 * a paginated delegate-card list is silently wrong past the first page. EX-40688
 * owns the real figure; design owns what the footer says once it exists.
 *
 * The sample-data pills and disclaimer that used to mark this were removed
 * deliberately (2026-08-06): they have to come out before production anyway, and
 * a half-accurate disclaimer is worse than none.
 *
 * MOUNTING IT:
 *
 * ```tsx
 * const history = useHistory()
 * // Both counts already exist at page level in the legacy AccountOverview.
 * const virtualCardCount = useSelector(selectCreditCardStatisticsActiveVcnCount)
 * const budgetCount = useSelector(selectCreditCardStatisticsBudgetCount)
 *
 * <CardOverviewStats
 *   creditCardId={creditCardId}
 *   showVirtualCards={tabAvailability.virtualCards}
 *   showBudgets={tabAvailability.budgets}
 *   virtualCardCount={virtualCardCount}
 *   budgetCount={budgetCount}
 *   adjustLimitHref={`${accountSettingsUrl(creditCardId)}#${settingsAnchors.spendLimit}`}
 *   virtualCardsHref={accountVirtualCardsUrl(creditCardId)}
 *   budgetsHref={accountBudgetsUrl(creditCardId)}
 *   onNavigate={(href) => history.push(href)}
 * />
 * ```
 */
export default function CardOverviewStats(
  props: CardOverviewStatsProps,
): ReactElement {
  const {
    creditCardId,
    showVirtualCards,
    showBudgets,
    virtualCardCount,
    budgetCount,
    adjustLimitHref,
    virtualCardsHref,
    budgetsHref,
    onNavigate,
    className,
  } = props

  const translate = useTranslate()
  const formatCurrency = useCurrencyFormatter()
  const {
    limits,
    limitsState,
    payment,
    paymentState,
    billingCycle,
    billingCycleState,
    // `paymentProvenance` / `billingCycleProvenance` are still on the seam (and
    // still covered by its own test) — the roll-up simply no longer renders a
    // surrogate caveat off them, per design QA.
  } = useCardOverviewStatsData(creditCardId)

  // The progress bar carries role="progressbar", which needs an accessible
  // name. Pointing it at the visible "Utilization" term keeps the two in sync
  // rather than duplicating the string into an aria-label.
  const utilizationLabelId = useId()

  // Only real figures render. `unavailable` and `error` both fall to the em
  // dash, exactly like every other cell below.
  const hasLimitFigures = limitsState === 'ready' && limits !== undefined

  const balanceValue = hasLimitFigures
    ? formatStatAmount(formatCurrency, limits.balance)
    : EM_DASH
  const limitValue = hasLimitFigures
    ? formatStatAmount(formatCurrency, limits.limit)
    : EM_DASH
  // `undefined` whenever there is no figure to show: either the limits could
  // not be read at all, or the limit is zero, which makes utilization undefined
  // rather than zero.
  const utilizationPercent = hasLimitFigures
    ? limits.utilizationPercent
    : undefined

  const utilizationValue =
    utilizationPercent !== undefined
      ? formatUtilization(utilizationPercent)
      : EM_DASH

  const countValue = (count: number | undefined): string =>
    count !== undefined ? String(count) : EM_DASH

  // `unavailable` and `error` both render as the em dash: it already means
  // "not available, for any reason" for every other cell on this roll-up, and
  // neither state has a fabricated figure to show instead.
  const paymentDueValue =
    paymentState === 'ready' && payment !== undefined
      ? payment === null
        ? translate('Nothing due')
        : formatStatAmount(formatCurrency, payment.amount)
      : EM_DASH
  const paymentDueSub =
    paymentState === 'ready' && payment !== undefined && payment !== null
      ? payment.status === 'PAST_DUE'
        ? translate('${date} • Past due', {
            date: formatStatDate(payment.dueDate),
          })
        : formatStatDate(payment.dueDate)
      : undefined

  const statementClosesValue =
    billingCycleState === 'ready' && billingCycle !== undefined
      ? formatStatDateShort(billingCycle.periodEnd)
      : EM_DASH

  return (
    <Box className={className} data-testid="cardOverviewStats">
      <dl
        css={style.statsRow}
        data-testid="cardOverviewStats-figures"
        // Announces the figures as still settling rather than as em dashes the
        // user should believe. Three independent sources, so any of them still
        // loading holds this true — otherwise the roll-up would announce em
        // dashes as final while the other two are still in flight.
        aria-busy={
          limitsState === 'loading' ||
          paymentState === 'loading' ||
          billingCycleState === 'loading'
        }
      >
        <StatCell
          testId="cardOverviewStats-balance"
          label={translate('Balance')}
          value={balanceValue}
          sub={translate('Current cycle')}
        />
        <StatCell
          testId="cardOverviewStats-paymentDue"
          label={translate('Payment due')}
          value={paymentDueValue}
          sub={paymentDueSub}
        />
        <StatCell
          testId="cardOverviewStats-statementCloses"
          label={translate('Statement closes')}
          value={statementClosesValue}
          sub={translate('Billing cycle')}
        />
        <StatCell
          testId="cardOverviewStats-cardLimit"
          label={translate('Card limit')}
          value={limitValue}
          sub={
            <StatNavLink href={adjustLimitHref} onNavigate={onNavigate}>
              {translate('Adjust limit →')}
            </StatNavLink>
          }
        />
        <StatCell
          testId="cardOverviewStats-utilization"
          labelId={utilizationLabelId}
          label={translate('Utilization')}
          value={utilizationValue}
          sub={
            // No bar at all when there is no figure. `ProgressBar` collapses an
            // absent value to zero, so rendering it regardless would announce
            // "Utilization, 0 percent" to a screen reader on exactly the cards
            // where the visible value is an em dash. That is the same mistake
            // `utilizationPercentOf` avoids — 0% reads as "plenty of room" on a
            // card that has none — just moved into the a11y layer.
            utilizationPercent !== undefined ? (
              <ProgressBar
                variant="percentage"
                value={utilizationPercent}
                aria-labelledby={utilizationLabelId}
                css={style.cellBar}
              />
            ) : undefined
          }
        />
        {showVirtualCards && (
          <StatCell
            testId="cardOverviewStats-virtualCards"
            label={translate('Virtual cards')}
            value={countValue(virtualCardCount)}
            sub={
              <StatNavLink href={virtualCardsHref} onNavigate={onNavigate}>
                {translate('View virtual cards →')}
              </StatNavLink>
            }
          />
        )}
      </dl>

      {showBudgets && (
        <div css={style.footer}>
          <span css={style.footerText}>
            {/*
              The count alone. This read "N budgets • $X allocated" until
              EX-40596, where the allocated total was the last fabricated figure
              on the roll-up: there is no aggregate for it anywhere in the API,
              and summing a paginated delegate-card list is silently wrong past
              the first page (EX-40688 owns the real one).

              The clause is dropped rather than rendered with a placeholder —
              a permanently empty "• — allocated" is noise, and design should
              decide what belongs here once a real figure exists.
            */}
            {budgetCount === undefined ? (
              // No count to pluralize on. The em dash says "unknown", which is
              // this roll-up's convention everywhere a figure is missing, and
              // the plural reads correctly beside it.
              translate('${count} budgets', { count: EM_DASH })
            ) : (
              <PluralTrns
                pluralSource={'${count} budgets'}
                singleSource={'1 budget'}
                count={budgetCount}
              />
            )}
          </span>
          <StatNavLink href={budgetsHref} onNavigate={onNavigate}>
            {translate('View budgets →')}
          </StatNavLink>
        </div>
      )}
    </Box>
  )
}
